import { useEffect, useState, useCallback } from 'react';
import { format } from 'date-fns';
import queryString from 'query-string';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  IconLoader,
  ButtonPseudo,
  ButtonSize,
  IconDownload,
  PermissionEnum,
  PermissionDenied,
  IconRefresh,
  DateInput,
  DateInputType,
  ToggleInput,
  ResponseError,
  getResponseErrors,
  AccountingJob,
  AccountingJobProps,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import CurrenciesPosition from '../../components/currencies-position/currencies-position';
import {
  useLazyGetCurrenciesPositionReportQuery,
  useExportCurrenciesPositionReportMutation,
} from './store/service';
import {
  useLazyGetAccountingJobsInProgressQuery,
  useLazyGetAccountingJobsInProgressStatesQuery,
} from '../../store/dictionaries/service';
import { setNotificationVisible } from '../../../../../store/slice';
import { CurrenciesPositionReport } from './types';
import { clearAccountingJobs } from '../../store/dictionaries/slice';

import cn from './currency-position.module.css';

let timeout: NodeJS.Timeout;

const CurrencyPositionHOC = () => {
  const [date, setDate] = useState<Date>();
  const [excludeFXGainsAndLossesAccounts, setExcludeFXGainsAndLossesAccounts] =
    useState(false);
  const [errorList, setErrorList] = useState('');
  const [currentAccountingJob, setCurrentAccountingJob] =
    useState<AccountingJobProps>();

  const {
    currenciesPosition: { currenciesPosition, formationTime },
    reportsDictionaries: { activePermissions, accountingJobs },
  } = useAppSelector((state) => state);

  const [getCurrenciesPositionReport, { isLoading, isFetching }] =
    useLazyGetCurrenciesPositionReportQuery();
  const [
    getAccountingJobs,
    {
      isLoading: accountingJobsIsLoading,
      isFetching: accountingJobsIsFetching,
    },
  ] = useLazyGetAccountingJobsInProgressQuery();
  const [getAccountingJobsStates] =
    useLazyGetAccountingJobsInProgressStatesQuery();

  const [
    exportCurrencyPosition,
    { isLoading: exportInProgress, isSuccess: exportIsSuccess },
  ] = useExportCurrenciesPositionReportMutation();

  const { search } = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const getCurrenciesPositionReportAction = useCallback(
    async (searchValue: string) => {
      const response = (await getCurrenciesPositionReport(
        searchValue,
      )) as unknown as {
        data?: CurrenciesPositionReport;
        error?: ResponseError;
        statusCode: number;
      };

      if (response?.error) {
        getResponseErrors({
          error: response.error,
          setErrorList,
        });
      }
    },
    [],
  );

  const onClearTimeout = async () => {
    await clearTimeout(timeout);
  };

  const onGetStates = async (query: string) => {
    await onClearTimeout();

    timeout = setTimeout(async () => {
      await getAccountingJobsStates(query);
      await onGetStates(query);
    }, 20000);
  };

  const onExportClick = async () => {
    const body: {
      transferDateFrom?: string;
      excludeFXGainsAndLossesAccounts?: boolean;
    } = {};

    if (date) {
      body.transferDateFrom = new Date(date).toISOString();
    }

    if (excludeFXGainsAndLossesAccounts) {
      body.excludeFXGainsAndLossesAccounts = excludeFXGainsAndLossesAccounts;
    }

    await exportCurrencyPosition(body);
  };

  const onChangeProfitLossFilter = ({ checked }: { checked: boolean }) => {
    setExcludeFXGainsAndLossesAccounts(checked);

    let queryValue = search
      ?.split('&')
      ?.filter((item) => !item.includes('excludeFXGainsAndLossesAccounts'))
      .join('&');

    if (checked) {
      queryValue = `${queryValue}&excludeFXGainsAndLossesAccounts=${checked}`;
    }

    navigate(`?${queryValue.replace('?', '')}`);
  };

  useEffect(() => {
    if (exportIsSuccess) {
      dispatch(
        setNotificationVisible({
          visible: true,
          text: "Report in progress. You'll be notified.",
          type: 'currency-position-report',
        }),
      );
    }
  }, [exportIsSuccess]);

  useEffect(() => {
    if (
      accountingJobs &&
      accountingJobs?.length &&
      !accountingJobsIsFetching &&
      !accountingJobsIsLoading
    ) {
      let currentJob: AccountingJobProps | undefined;

      if (accountingJobs?.find((item) => item.state === 'IN_PROGRESS')) {
        currentJob = accountingJobs?.find(
          (item) => item.state === 'IN_PROGRESS',
        );
      } else if (
        !accountingJobs?.find((item) => item.state === 'IN_PROGRESS') &&
        accountingJobs?.find((item) => item.state === 'QUEUED')
      ) {
        currentJob = accountingJobs?.find((item) => item.state === 'QUEUED');
      } else if (
        !accountingJobs?.find((item) => item.state === 'IN_PROGRESS') &&
        !accountingJobs?.find((item) => item.state === 'QUEUED') &&
        accountingJobs?.find((item) => item.state === 'NEW')
      ) {
        currentJob = accountingJobs?.find((item) => item.state === 'NEW');
      }

      if (currentJob) {
        setCurrentAccountingJob(currentJob);
      }
    }
  }, [accountingJobs, accountingJobsIsFetching, accountingJobsIsLoading]);

  useEffect(() => {
    if (
      activePermissions?.includes(PermissionEnum.REPORTS_ACCOUNTING_JOBS_LITS)
    ) {
      getAccountingJobs(
        '?type=BALANCES_LOGS&type=BALANCE_SHEET&state=IN_PROGRESS&state=QUEUED&state=NEW&all=true',
      );

      onGetStates(
        '?type=BALANCES_LOGS&type=BALANCE_SHEET&state=IN_PROGRESS&state=QUEUED&state=NEW&all=true',
      );
    }
  }, [activePermissions]);

  useEffect(
    () => () => {
      dispatch(clearAccountingJobs());

      setTimeout(async () => {
        await onClearTimeout();
      });
    },
    [],
  );

  useEffect(() => {
    if (
      activePermissions &&
      activePermissions?.includes(
        PermissionEnum.REPORTS_CURRENCY_POSITION_LIST,
      ) &&
      !search
    ) {
      const currentDate = new Date();

      currentDate.setHours(0);
      currentDate.setMinutes(0);
      currentDate.setSeconds(0);
      currentDate.setMilliseconds(0);

      navigate(
        `?transferDateFrom=${format(new Date(currentDate), 'yyyy-MM-dd')}`,
      );

      setDate(new Date(currentDate));
    }
  }, [activePermissions, search]);

  useEffect(() => {
    if (
      activePermissions &&
      activePermissions?.includes(
        PermissionEnum.REPORTS_CAPITAL_ADEQUACY_LIST,
      ) &&
      search
    ) {
      const parsed = queryString.parse(search);

      setDate(new Date(parsed?.transferDateFrom as string));

      if (parsed.excludeFXGainsAndLossesAccounts) {
        setExcludeFXGainsAndLossesAccounts(true);
      }

      getCurrenciesPositionReportAction(search);
    }
  }, [activePermissions, search]);

  useEffect(() => {
    let title = 'Currency position report';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, []);

  let globalContent = (
    <div className={cn.globalLoader}>
      <IconLoader />
    </div>
  );

  if (activePermissions) {
    if (
      !activePermissions.includes(
        PermissionEnum.REPORTS_CURRENCY_POSITION_LIST,
      ) ||
      !activePermissions.includes(PermissionEnum.SECTION_REPORTS)
    ) {
      globalContent = <PermissionDenied />;
    } else if (
      activePermissions.includes(PermissionEnum.REPORTS_CURRENCY_POSITION_LIST)
    ) {
      globalContent = (
        <div className={cn.currencyPosition}>
          <div className={cn.currencyPositionTop}>
            <div className={cn.currencyPositionTitle}>
              Currency position report
            </div>
            <div className={cn.currencyPositionTopControls}>
              <div className={cn.currencyPositionTopControlsLeft}>
                <DateInput
                  type={DateInputType.PSEUDO}
                  placeholder="Select Date"
                  value={date?.toISOString()}
                  className={cn.currencyPositionDateInput}
                  onReset={() => {
                    navigate('');
                    setDate(undefined);
                  }}
                  onChange={(e) => {
                    setDate(new Date(e));

                    navigate(
                      `?transferDateFrom=${format(new Date(e), 'yyyy-MM-dd')}${
                        excludeFXGainsAndLossesAccounts
                          ? `&excludeFXGainsAndLossesAccounts=${excludeFXGainsAndLossesAccounts}`
                          : ''
                      }`,
                    );
                  }}
                />
                <div className={cn.toggleWrapper}>
                  <ToggleInput
                    name="excludeFXGainsAndLossesAccounts"
                    title="Without profit & loss"
                    checked={excludeFXGainsAndLossesAccounts}
                    onChange={(e) =>
                      onChangeProfitLossFilter({
                        checked: e.target.checked,
                      })
                    }
                    disabled={Boolean(errorList)}
                    visualClassName={cn.currencyPositionToggle}
                  />
                </div>
              </div>
              <div className={cn.currencyPositionTopControlsRight}>
                <ButtonPseudo
                  text="Refresh"
                  icon={
                    <IconRefresh
                      className={
                        currenciesPosition && isFetching ? cn.refreshing : ''
                      }
                    />
                  }
                  className={`button-white ${cn.currencyPositionRefreshButton}`}
                  size={ButtonSize.SMALL}
                  onClick={() => {
                    getCurrenciesPositionReportAction(search);
                    setErrorList('');
                  }}
                  disabled={isLoading || isFetching}
                />
                {activePermissions?.includes(
                  PermissionEnum.REPORTS_CURRENCY_POSITION_EXPORT_ACTION,
                ) && (
                  <ButtonPseudo
                    text={exportInProgress ? 'Exporting...' : 'Download Report'}
                    icon={!exportInProgress ? <IconDownload /> : undefined}
                    className="button-white"
                    size={ButtonSize.SMALL}
                    onClick={onExportClick}
                    disabled={Boolean(errorList)}
                  />
                )}
              </div>
            </div>
            <div className={cn.currencyPositionSearchInfo}>
              {formationTime?.end
                ? `Formed in ${formationTime?.duration} sec: ${new Date(
                    formationTime?.start || '',
                  ).toLocaleDateString('en-US', {
                    month: 'short',
                    year: 'numeric',
                    day: 'numeric',
                  })} from ${new Date(formationTime?.start || '')
                    .toLocaleString()
                    .slice(-8)} to ${new Date(formationTime?.end)
                    .toLocaleString()
                    .slice(-8)}`
                : 'Forming...'}
            </div>
            {activePermissions?.includes(
              PermissionEnum.REPORTS_ACCOUNTING_JOBS_LITS,
            ) && (
              <AccountingJob
                accountingJobsIsLoading={
                  accountingJobsIsLoading || accountingJobsIsFetching
                }
                accountingJob={currentAccountingJob}
                accountingJobs={accountingJobs}
                jobsLength={accountingJobs?.length}
                query="?type=BALANCES_LOGS&type=BALANCE_SHEET&state=IN_PROGRESS&state=QUEUED&state=NEW"
              />
            )}
          </div>
          {errorList && (
            <div className={cn.reportsLoadingError}>
              <div className={cn.reportsLoadingErrorTitle}>
                Unable to load data
              </div>
              <div className={cn.reportsLoadingErrorText}>{errorList}</div>
            </div>
          )}
          {!errorList && (
            <CurrenciesPosition
              currenciesPosition={currenciesPosition}
              isLoading={isLoading || isFetching}
            />
          )}
        </div>
      );
    }
  }

  return globalContent;
};

export default CurrencyPositionHOC;
